import { useQuery } from "@apollo/client";
import { useModal } from "@ebay/nice-modal-react";
import { useEffect, useMemo } from "react";
import Skeleton from "react-loading-skeleton";

import DrawerSimple from "ds/components/DrawerNew/Simple";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import Box from "ds/components/Box";
import { PERMISSIONS } from "shared/Actions/constants";

import PermissionsTable from "./PermissionsTable";
import { PREVIEW_PERMISSIONS_ROLE } from "./gql";

type PreviewPermissionsDrawerProps = {
  roleId: string;
};

const SKELETON_ROW_COUNT = 6;

const PreviewPermissionsDrawer = createDrawer<PreviewPermissionsDrawerProps>(({ roleId }) => {
  const modal = useModal();

  const { data, loading, error } = useQuery(PREVIEW_PERMISSIONS_ROLE, {
    variables: { id: roleId },
    fetchPolicy: "cache-first",
  });

  // The global Apollo error link already surfaces the failure as a flash, so close the
  // drawer instead of duplicating the error state inline.
  useEffect(() => {
    if (error) {
      modal.hide();
    }
  }, [error, modal]);

  const role = data?.role;

  const permissions = useMemo(
    () => role?.actions.map((action) => PERMISSIONS[action]) ?? [],
    [role?.actions]
  );

  return (
    <DrawerSimple variant="wide" isDismissable>
      <DrawerHeader justify="between">
        <DrawerHeaderTitle title="Preview permissions" />
        <DrawerCloseIcon />
      </DrawerHeader>
      <DrawerBody>
        {loading && !role && (
          <Box direction="column" gap="medium">
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
              <Skeleton key={index} height={20} />
            ))}
          </Box>
        )}

        {role && (
          <PermissionsTable
            permissions={permissions}
            ariaLabel={`Permissions for ${role.name} role`}
          />
        )}
      </DrawerBody>
      <DrawerFooter>
        <DrawerFooterActions>
          <DrawerCancelButton text="Close" />
        </DrawerFooterActions>
      </DrawerFooter>
    </DrawerSimple>
  );
});

export const showPreviewPermissionsDrawer = createDrawerTrigger(PreviewPermissionsDrawer);
